import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FiArrowLeft, FiPlus, FiEdit2, FiTrash2, FiRotateCcw, FiCopy, FiMove, FiX, FiAlertTriangle,
} from 'react-icons/fi';
import {
  listarTipos, criarTipo, atualizarTipo, excluirTipo,
  criarEtapaTipo, atualizarEtapaTipo, excluirEtapaTipo, reordenarEtapasTipo,
} from '../../services/processoTipos';
import { listarProcessos } from '../../services/processos';
import ConfirmarModal from './ConfirmarModal';
import styles from './ConfigurarEtapas.module.css';
import useEsc from '../../hooks/useEsc';

/* quantos processos daquele tipo estão em andamento/travados -- usado só
   pra avisar "X processos serão afetados" antes de mexer no catálogo */
async function contarProcessosAtivos(chave) {
  const [emAndamento, travados] = await Promise.all([
    listarProcessos({ tipo: chave, status: 'em_andamento' }),
    listarProcessos({ tipo: chave, status: 'travado' }),
  ]);
  return (emAndamento.items?.length || 0) + (travados.items?.length || 0);
}

function ListaDinamica({ label, itens, onChange, placeholder }) {
  function set(i, valor) {
    const novo = [...itens]; novo[i] = valor; onChange(novo);
  }
  function remover(i) { onChange(itens.filter((_, idx) => idx !== i)); }
  function adicionar() { onChange([...itens, '']); }

  return (
    <div className={styles.campo}>
      <span className={styles.label}>{label}</span>
      <div className={styles.listaDinamica}>
        {itens.map((item, i) => (
          <div key={i} className={styles.listaDinamicaItem}>
            <input className={styles.input} value={item} onChange={e => set(i, e.target.value)} placeholder={placeholder} />
            <button type="button" className={styles.btnRemoverItem} onClick={() => remover(i)}><FiX size={14} /></button>
          </div>
        ))}
        <button type="button" className={styles.btnAdicionarItem} onClick={adicionar}>
          <FiPlus size={12} /> Adicionar item
        </button>
      </div>
    </div>
  );
}

function TipoFormModal({ tipo, onFechar, onSalvo }) {
  useEsc(onFechar);
  const editando = !!tipo;
  const [chave, setChave]         = useState(tipo?.chave || '');
  const [nome, setNome]           = useState(tipo?.nome || '');
  const [descricao, setDescricao] = useState(tipo?.descricao || '');
  const [salvando, setSalvando]   = useState(false);
  const [erro, setErro]           = useState('');
  const [avisoProcessos, setAvisoProcessos] = useState(0);

  useEffect(() => {
    if (editando) contarProcessosAtivos(tipo.chave).then(setAvisoProcessos).catch(() => {});
  }, [editando, tipo]);

  async function salvar() {
    if (!editando && !chave.trim()) return setErro('Informe uma chave para o tipo.');
    if (!nome.trim()) return setErro('Nome é obrigatório.');
    setSalvando(true); setErro('');
    try {
      if (editando) {
        const atualizado = await atualizarTipo(tipo.id, { nome, descricao });
        onSalvo({ ...tipo, ...atualizado });
      } else {
        const novo = await criarTipo({ chave, nome, descricao });
        onSalvo(novo);
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitulo}>{editando ? 'Editar tipo' : 'Novo tipo de processo'}</h2>
          <button className={styles.btnFechar} onClick={onFechar} type="button"><FiX size={18} /></button>
        </div>
        <div className={styles.form}>
          {erro && <p className={styles.erro}>{erro}</p>}
          {avisoProcessos > 0 && (
            <div className={styles.aviso}>
              <FiAlertTriangle size={14} />
              {avisoProcessos} processo{avisoProcessos === 1 ? '' : 's'} em andamento deste tipo será{avisoProcessos === 1 ? '' : 'ão'} afetado{avisoProcessos === 1 ? '' : 's'}.
            </div>
          )}
          {!editando && (
            <div className={styles.campo}>
              <span className={styles.label}>Chave (identificador interno, sem espaços)</span>
              <input className={styles.input} value={chave} onChange={e => setChave(e.target.value)} placeholder="ex.: consultoria" />
            </div>
          )}
          <div className={styles.campo}>
            <span className={styles.label}>Nome</span>
            <input className={styles.input} value={nome} onChange={e => setNome(e.target.value)} />
          </div>
          <div className={styles.campo}>
            <span className={styles.label}>Descrição (opcional)</span>
            <textarea className={styles.textarea} rows={3} value={descricao} onChange={e => setDescricao(e.target.value)} />
          </div>
          <div className={styles.modalFooter}>
            <button className={styles.btnCancelar} onClick={onFechar} type="button">Cancelar</button>
            <button className={styles.btnSalvar} onClick={salvar} disabled={salvando} type="button">
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EtapaFormModal({ tipoId, etapa, avisoProcessos, onFechar, onSalvo }) {
  useEsc(onFechar);
  const editando = !!etapa;
  const [nome, setNome]           = useState(etapa?.nome || '');
  const [objetivo, setObjetivo]   = useState(etapa?.objetivo || '');
  const [comoFazer, setComoFazer] = useState(etapa?.como_fazer?.length ? etapa.como_fazer : ['']);
  const [documentos, setDocumentos] = useState(etapa?.documentos?.length ? etapa.documentos : ['']);
  const [escalar, setEscalar]     = useState(etapa?.escalar || '');
  const [salvando, setSalvando]   = useState(false);
  const [erro, setErro]           = useState('');

  async function salvar() {
    if (!nome.trim()) return setErro('Nome é obrigatório.');
    setSalvando(true); setErro('');
    const payload = {
      nome,
      objetivo,
      como_fazer: comoFazer.map(s => s.trim()).filter(Boolean),
      documentos: documentos.map(s => s.trim()).filter(Boolean),
      escalar,
    };
    try {
      if (editando) {
        const atualizada = await atualizarEtapaTipo(tipoId, etapa.id, payload);
        onSalvo(atualizada);
      } else {
        const nova = await criarEtapaTipo(tipoId, payload);
        onSalvo(nova);
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className={styles.overlay}>
      <div className={`${styles.modal} ${styles.modalGrande}`} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitulo}>{editando ? 'Editar etapa' : 'Nova etapa'}</h2>
          <button className={styles.btnFechar} onClick={onFechar} type="button"><FiX size={18} /></button>
        </div>
        <div className={styles.form}>
          {erro && <p className={styles.erro}>{erro}</p>}
          {avisoProcessos > 0 && (
            <div className={styles.aviso}>
              <FiAlertTriangle size={14} />
              {avisoProcessos} processo{avisoProcessos === 1 ? '' : 's'} em andamento deste tipo será{avisoProcessos === 1 ? '' : 'ão'} afetado{avisoProcessos === 1 ? '' : 's'}.
            </div>
          )}
          <div className={styles.campo}>
            <span className={styles.label}>Nome da etapa</span>
            <input className={styles.input} value={nome} onChange={e => setNome(e.target.value)} />
          </div>
          <div className={styles.campo}>
            <span className={styles.label}>Objetivo</span>
            <textarea className={styles.textarea} rows={2} value={objetivo} onChange={e => setObjetivo(e.target.value)} placeholder="O que essa etapa entrega" />
          </div>
          <ListaDinamica label="Como fazer" itens={comoFazer} onChange={setComoFazer} placeholder="Um passo do procedimento" />
          <ListaDinamica label="Documentos" itens={documentos} onChange={setDocumentos} placeholder="Um documento necessário" />
          <div className={styles.campo}>
            <span className={styles.label}>Quando parar e perguntar</span>
            <textarea className={styles.textarea} rows={2} value={escalar} onChange={e => setEscalar(e.target.value)} placeholder="Em que situação escalar pra coordenação" />
          </div>
          <div className={styles.modalFooter}>
            <button className={styles.btnCancelar} onClick={onFechar} type="button">Cancelar</button>
            <button className={styles.btnSalvar} onClick={salvar} disabled={salvando} type="button">
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfigurarEtapas({ onVoltar }) {
  const [tipos, setTipos]           = useState([]);
  const [tipoAtivoId, setTipoAtivoId] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro]             = useState('');

  const [mostrarNovoTipo, setMostrarNovoTipo] = useState(false);
  const [editandoTipo, setEditandoTipo]       = useState(null);
  const [editandoEtapa, setEditandoEtapa]     = useState(null);
  const [criandoEtapa, setCriandoEtapa]       = useState(false);
  const [avisoTipoAtivo, setAvisoTipoAtivo]   = useState(0);
  const [excluindoTipo, setExcluindoTipo]     = useState(null);
  const [excluindoEtapa, setExcluindoEtapa]   = useState(null);
  const [processando, setProcessando]         = useState(false);
  const [erroAcao, setErroAcao]               = useState('');

  const arrastandoIdx = useRef(null);
  const [ordemLocal, setOrdemLocal] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true); setErro('');
    listarTipos()
      .then(r => {
        const items = r.items || [];
        setTipos(items);
        setTipoAtivoId(prev => prev && items.some(t => t.id === prev) ? prev : (items[0]?.id ?? null));
      })
      .catch(e => setErro(e.message))
      .finally(() => setCarregando(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const tipoAtivo = tipos.find(t => t.id === tipoAtivoId) || null;
  const etapasAtivas = ordemLocal || (tipoAtivo?.etapas || []).filter(e => e.ativo).sort((a, b) => a.ordem - b.ordem);
  const etapasInativas = (tipoAtivo?.etapas || []).filter(e => !e.ativo).sort((a, b) => a.ordem - b.ordem);

  useEffect(() => { setOrdemLocal(null); }, [tipoAtivoId]);

  function aoCriarTipo(novo) {
    setTipos(prev => [...prev, novo]);
    setTipoAtivoId(novo.id);
    setMostrarNovoTipo(false);
  }

  function aoSalvarTipo(atualizado) {
    setTipos(prev => prev.map(t => t.id === atualizado.id ? { ...t, ...atualizado } : t));
    setEditandoTipo(null);
  }

  async function alternarAtivoTipo(tipo) {
    setProcessando(true); setErroAcao('');
    try {
      const atualizado = tipo.ativo ? await excluirTipo(tipo.id) : await atualizarTipo(tipo.id, { ativo: true });
      setTipos(prev => prev.map(t => t.id === tipo.id ? { ...t, ...atualizado } : t));
    } catch (e) {
      setErroAcao(e.message);
    } finally {
      setProcessando(false);
    }
  }

  async function duplicarTipo(tipo) {
    setProcessando(true); setErroAcao('');
    try {
      const base = tipo.chave + '_copia';
      const chaveEmUso = (candidata) => tipos.some(t => t.chave === candidata);
      let chave = base;
      let sufixo = 2;
      while (chaveEmUso(chave)) { chave = `${base}${sufixo}`; sufixo += 1; }

      const novo = await criarTipo({ chave, nome: `${tipo.nome} (cópia)`, descricao: tipo.descricao });
      const etapasCriadas = [];
      for (const etapa of (tipo.etapas || []).filter(e => e.ativo).sort((a, b) => a.ordem - b.ordem)) {
        const criada = await criarEtapaTipo(novo.id, {
          nome: etapa.nome, objetivo: etapa.objetivo,
          como_fazer: etapa.como_fazer, documentos: etapa.documentos, escalar: etapa.escalar,
        });
        etapasCriadas.push(criada);
      }
      const tipoCompleto = { ...novo, etapas: etapasCriadas };
      setTipos(prev => [...prev, tipoCompleto]);
      setTipoAtivoId(tipoCompleto.id);
    } catch (e) {
      setErroAcao(e.message);
    } finally {
      setProcessando(false);
    }
  }

  function aoSalvarEtapa(atualizada) {
    setTipos(prev => prev.map(t => {
      if (t.id !== tipoAtivoId) return t;
      const existe = (t.etapas || []).some(e => e.id === atualizada.id);
      const etapas = existe
        ? t.etapas.map(e => e.id === atualizada.id ? atualizada : e)
        : [...(t.etapas || []), atualizada];
      return { ...t, etapas };
    }));
    setEditandoEtapa(null);
    setCriandoEtapa(false);
  }

  async function confirmarExcluirTipo() {
    setProcessando(true); setErroAcao('');
    try {
      const atualizado = await excluirTipo(excluindoTipo.id);
      setTipos(prev => prev.map(t => t.id === excluindoTipo.id ? { ...t, ...atualizado } : t));
      setExcluindoTipo(null);
    } catch (e) {
      setErroAcao(e.message);
    } finally {
      setProcessando(false);
    }
  }

  async function confirmarExcluirEtapa() {
    setProcessando(true); setErroAcao('');
    try {
      const resultado = await excluirEtapaTipo(tipoAtivoId, excluindoEtapa.id);
      setTipos(prev => prev.map(t => {
        if (t.id !== tipoAtivoId) return t;
        if (!resultado) return { ...t, etapas: (t.etapas || []).filter(e => e.id !== excluindoEtapa.id) };
        return { ...t, etapas: (t.etapas || []).map(e => e.id === excluindoEtapa.id ? resultado : e) };
      }));
      setExcluindoEtapa(null);
    } catch (e) {
      setErroAcao(e.message);
    } finally {
      setProcessando(false);
    }
  }

  function abrirEdicaoEtapa(etapa) {
    contarProcessosAtivos(tipoAtivo.chave).then(setAvisoTipoAtivo).catch(() => setAvisoTipoAtivo(0));
    setEditandoEtapa(etapa);
  }
  function abrirCriacaoEtapa() {
    contarProcessosAtivos(tipoAtivo.chave).then(setAvisoTipoAtivo).catch(() => setAvisoTipoAtivo(0));
    setCriandoEtapa(true);
  }

  function aoArrastarInicio(idx) { arrastandoIdx.current = idx; }
  function aoArrastarSobre(e) { e.preventDefault(); }
  function aoSoltar(idx) {
    const origem = arrastandoIdx.current;
    if (origem === null || origem === idx) return;
    const nova = [...etapasAtivas];
    const [movida] = nova.splice(origem, 1);
    nova.splice(idx, 0, movida);
    setOrdemLocal(nova);
    arrastandoIdx.current = null;

    reordenarEtapasTipo(tipoAtivoId, nova.map(e => e.id))
      .then(r => {
        setTipos(prev => prev.map(t => {
          if (t.id !== tipoAtivoId) return t;
          const inativas = (t.etapas || []).filter(e => !e.ativo);
          return { ...t, etapas: [...(r.items || []), ...inativas] };
        }));
        setOrdemLocal(null);
      })
      .catch(e => { setErroAcao(e.message); setOrdemLocal(null); });
  }

  if (carregando) return <div className={styles.page}><p className={styles.vazio}>Carregando...</p></div>;

  return (
    <div className={styles.page}>
      <button className={styles.btnVoltar} onClick={onVoltar} type="button"><FiArrowLeft size={14} /> Voltar</button>

      <div className={styles.pageHeader}>
        <h1 className={styles.titulo}>Configurar etapas</h1>
      </div>

      {erro && <div className={styles.erroFaixa}>{erro}</div>}
      {erroAcao && <div className={styles.erroFaixa}>{erroAcao}</div>}

      <div className={styles.layout}>
        <div className={styles.colunaTipos}>
          <button className={styles.btnNovo} onClick={() => setMostrarNovoTipo(true)} type="button">
            <FiPlus size={14} /> Novo tipo
          </button>
          <div className={styles.listaTipos}>
            {tipos.map(t => (
              <button
                key={t.id}
                type="button"
                className={`${styles.tipoItem} ${t.id === tipoAtivoId ? styles.tipoItemAtivo : ''} ${!t.ativo ? styles.tipoItemInativo : ''}`}
                onClick={() => setTipoAtivoId(t.id)}
              >
                <span className={styles.tipoItemNome}>{t.nome}</span>
                {!t.ativo && <span className={styles.tipoItemBadgeInativo}>Inativo</span>}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.colunaEtapas}>
          {!tipoAtivo ? (
            <p className={styles.vazio}>Crie um tipo pra começar.</p>
          ) : (
            <>
              <div className={styles.tipoCabecalho}>
                <div>
                  <h2 className={styles.tipoNome}>{tipoAtivo.nome}</h2>
                  {tipoAtivo.descricao && <p className={styles.tipoDescricao}>{tipoAtivo.descricao}</p>}
                </div>
                <div className={styles.tipoAcoes}>
                  <button className={styles.btnSecundario} onClick={() => setEditandoTipo(tipoAtivo)} type="button">
                    <FiEdit2 size={13} /> Editar
                  </button>
                  <button className={styles.btnSecundario} onClick={() => duplicarTipo(tipoAtivo)} disabled={processando} type="button">
                    <FiCopy size={13} /> Duplicar
                  </button>
                  {tipoAtivo.ativo ? (
                    <button className={styles.btnSecundario} onClick={() => setExcluindoTipo(tipoAtivo)} type="button">
                      <FiTrash2 size={13} /> Desativar
                    </button>
                  ) : (
                    <button className={styles.btnSecundario} onClick={() => alternarAtivoTipo(tipoAtivo)} disabled={processando} type="button">
                      <FiRotateCcw size={13} /> Reativar
                    </button>
                  )}
                </div>
              </div>

              <div className={styles.etapasHeader}>
                <h3 className={styles.secaoTitulo}>Etapas ativas</h3>
                <button className={styles.btnNovo} onClick={abrirCriacaoEtapa} type="button">
                  <FiPlus size={13} /> Nova etapa
                </button>
              </div>

              {etapasAtivas.length === 0 ? (
                <p className={styles.vazio}>Nenhuma etapa ativa ainda.</p>
              ) : (
                <div className={styles.etapasLista}>
                  {etapasAtivas.map((etapa, i) => (
                    <div
                      key={etapa.id}
                      className={styles.etapaCard}
                      draggable
                      onDragStart={() => aoArrastarInicio(i)}
                      onDragOver={aoArrastarSobre}
                      onDrop={() => aoSoltar(i)}
                    >
                      <span className={styles.arrastarAlca}><FiMove size={14} /></span>
                      <span className={styles.etapaCardNumero}>{i + 1}</span>
                      <span className={styles.etapaCardNome}>{etapa.nome}</span>
                      <div className={styles.etapaCardAcoes}>
                        <button className={styles.acaoBtn} title="Editar" onClick={() => abrirEdicaoEtapa(etapa)} type="button">
                          <FiEdit2 size={14} />
                        </button>
                        <button className={styles.acaoBtn} title="Desativar" onClick={() => setExcluindoEtapa(etapa)} type="button">
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {etapasInativas.length > 0 && (
                <>
                  <h3 className={styles.secaoTitulo}>Etapas inativas</h3>
                  <div className={styles.etapasLista}>
                    {etapasInativas.map(etapa => (
                      <div key={etapa.id} className={`${styles.etapaCard} ${styles.etapaCardInativa}`}>
                        <span className={styles.etapaCardNome}>{etapa.nome}</span>
                        <div className={styles.etapaCardAcoes}>
                          <button
                            className={styles.acaoBtn}
                            title="Reativar"
                            onClick={() => atualizarEtapaTipo(tipoAtivoId, etapa.id, { ativo: true }).then(aoSalvarEtapa).catch(e => setErroAcao(e.message))}
                            type="button"
                          >
                            <FiRotateCcw size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {mostrarNovoTipo && (
        <TipoFormModal onFechar={() => setMostrarNovoTipo(false)} onSalvo={aoCriarTipo} />
      )}

      {editandoTipo && (
        <TipoFormModal tipo={editandoTipo} onFechar={() => setEditandoTipo(null)} onSalvo={aoSalvarTipo} />
      )}

      {(criandoEtapa || editandoEtapa) && (
        <EtapaFormModal
          tipoId={tipoAtivoId}
          etapa={editandoEtapa}
          avisoProcessos={avisoTipoAtivo}
          onFechar={() => { setCriandoEtapa(false); setEditandoEtapa(null); }}
          onSalvo={aoSalvarEtapa}
        />
      )}

      {excluindoTipo && (
        <ConfirmarModal
          titulo="Desativar tipo"
          mensagem={`"${excluindoTipo.nome}" some do seletor de "Novo processo", mas os processos já existentes continuam funcionando normalmente.`}
          textoConfirmar="Desativar"
          perigo
          confirmando={processando}
          onConfirmar={confirmarExcluirTipo}
          onCancelar={() => setExcluindoTipo(null)}
        />
      )}

      {excluindoEtapa && (
        <ConfirmarModal
          titulo="Desativar etapa"
          mensagem={`"${excluindoEtapa.nome}" some do checklist de processos novos. Processos que já passaram por essa etapa mantêm o histórico intacto.`}
          textoConfirmar="Desativar"
          perigo
          confirmando={processando}
          onConfirmar={confirmarExcluirEtapa}
          onCancelar={() => setExcluindoEtapa(null)}
        />
      )}
    </div>
  );
}

export default ConfigurarEtapas;
